import * as THREE from "three";
import { normalizeTimeOfDay } from "../../domain/viewer/timeOfDay";
import type { TimeOfDay, ViewerRuntimeProfile } from "./viewerConfig";

export interface HeaterAnimationTargets {
	fireLights: THREE.SpotLight[];
	stoneLights: THREE.PointLight[];
	emberLights: THREE.PointLight[];
	emissiveMaterials: Array<
		THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial
	>;
	stoneMaterials: Array<
		THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial
	>;
	steamSprites: THREE.Sprite[];
}

export function createEmptyHeaterAnimationTargets(): HeaterAnimationTargets {
	return {
		fireLights: [],
		stoneLights: [],
		emberLights: [],
		emissiveMaterials: [],
		stoneMaterials: [],
		steamSprites: [],
	};
}

function isEmissiveMaterial(
	material: THREE.Material,
): material is THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial {
	return "emissiveIntensity" in material;
}

export function collectHeaterAnimationTargets(
	root: THREE.Object3D | null,
): HeaterAnimationTargets {
	const nextTargets = createEmptyHeaterAnimationTargets();
	const emissiveMaterials = new Set<
		THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial
	>();
	const stoneMaterials = new Set<
		THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial
	>();

	root?.traverse((child) => {
		if (
			child instanceof THREE.SpotLight &&
			child.name.includes("-fire-glow")
		) {
			child.userData.basePosition ??= child.position.clone();
			nextTargets.fireLights.push(child);
		}
		if (
			child instanceof THREE.PointLight &&
			child.name.includes("-stone-glow")
		) {
			child.userData.basePosition ??= child.position.clone();
			nextTargets.stoneLights.push(child);
		}
		if (
			child instanceof THREE.PointLight &&
			child.name.includes("-ember-glow")
		) {
			child.userData.basePosition ??= child.position.clone();
			nextTargets.emberLights.push(child);
		}
		if (child instanceof THREE.Sprite && child.name.includes("-steam-")) {
			nextTargets.steamSprites.push(child);
		}
		if (
			child instanceof THREE.Mesh &&
			child.material instanceof THREE.Material &&
			isEmissiveMaterial(child.material)
		) {
			if (child.name.includes("-door-glass")) {
				emissiveMaterials.add(child.material);
			}
			if (
				child.name.includes("-stones") ||
				child.name.includes("-stone-top")
			) {
				stoneMaterials.add(child.material);
			}
		}
	});

	nextTargets.emissiveMaterials = [...emissiveMaterials];
	nextTargets.stoneMaterials = [...stoneMaterials];
	return nextTargets;
}

export function resetHeaterAnimationTargets(
	targets: HeaterAnimationTargets,
	timeOfDay: TimeOfDay,
	runtimeProfile: ViewerRuntimeProfile,
) {
	const eveningMix = Math.max(0, (normalizeTimeOfDay(timeOfDay) - 0.68) / 0.32);
	const fireBase = 2.75 + eveningMix * 0.35;
	const stoneBase = 0.1 + eveningMix * 0.1;
	const glassBase = 0.55 + eveningMix * 0.3;
	const emberBase = 0.85 + eveningMix * 0.2;
	const stoneMaterialBase = 0.09 + eveningMix * 0.06;

	for (const light of targets.fireLights) {
		light.intensity = runtimeProfile.heaterAnimationEnabled ? fireBase : 1.8;
		if (light.userData.basePosition instanceof THREE.Vector3) {
			light.position.copy(light.userData.basePosition);
		}
	}
	for (const light of targets.stoneLights) {
		light.intensity = runtimeProfile.heaterAnimationEnabled ? stoneBase : 0.16;
		if (light.userData.basePosition instanceof THREE.Vector3) {
			light.position.copy(light.userData.basePosition);
		}
	}
	for (const light of targets.emberLights) {
		light.intensity = runtimeProfile.heaterAnimationEnabled ? emberBase : 0;
		if (light.userData.basePosition instanceof THREE.Vector3) {
			light.position.copy(light.userData.basePosition);
		}
	}
	for (const material of targets.emissiveMaterials) {
		material.emissiveIntensity = runtimeProfile.heaterAnimationEnabled
			? glassBase
			: 0.45;
	}
	for (const material of targets.stoneMaterials) {
		material.emissiveIntensity = runtimeProfile.heaterAnimationEnabled
			? stoneMaterialBase
			: 0.06;
	}
	for (const sprite of targets.steamSprites) {
		const material = sprite.material;
		material.opacity = runtimeProfile.steamEnabled ? 0.028 : 0;
		sprite.position.set(
			sprite.userData.baseX ?? sprite.position.x,
			sprite.userData.baseY ?? sprite.position.y,
			sprite.userData.baseZ ?? sprite.position.z,
		);
		sprite.scale.set(0.22, 0.3, 1);
	}
}

export function shouldAnimateHeaterAmbience(
	runtimeProfile: ViewerRuntimeProfile,
	timeOfDay: TimeOfDay,
	targets: HeaterAnimationTargets,
): boolean {
	const hasTargets =
		targets.fireLights.length > 0 || targets.steamSprites.length > 0;
	if (!hasTargets) return false;
	if (normalizeTimeOfDay(timeOfDay) < 0.68) return false;
	return runtimeProfile.heaterAnimationEnabled || runtimeProfile.steamEnabled;
}

export function animateHeaterAmbienceFrame(
	targets: HeaterAnimationTargets,
	runtimeProfile: ViewerRuntimeProfile,
	timeOfDay: TimeOfDay,
	elapsedTime: number,
): boolean {
	if (!shouldAnimateHeaterAmbience(runtimeProfile, timeOfDay, targets)) {
		return false;
	}

	const t = elapsedTime;
	const eveningMix = Math.max(0, (normalizeTimeOfDay(timeOfDay) - 0.68) / 0.32);
	const fireBase = 2.75 + eveningMix * 0.35;
	const stoneBase = 0.1 + eveningMix * 0.1;
	const emberBase = 0.85 + eveningMix * 0.2;
	const firePulse =
		0.84 + Math.sin(t * 7.2) * 0.08 + Math.sin(t * 13.7 + 1.1) * 0.04;
	const stonePulse = 0.78 + Math.sin(t * 1.8 + 0.4) * 0.12;

	if (runtimeProfile.heaterAnimationEnabled) {
		for (const [index, light] of targets.fireLights.entries()) {
			const basePosition = light.userData.basePosition as
				| THREE.Vector3
				| undefined;
			light.intensity = fireBase * firePulse;
			if (basePosition) {
				light.position.set(
					basePosition.x + Math.sin(t * 2.6 + index) * 0.004,
					basePosition.y + Math.sin(t * 3.4 + index * 0.7) * 0.005,
					basePosition.z + Math.cos(t * 2.1 + index) * 0.003,
				);
			}
		}
		for (const light of targets.stoneLights) {
			light.intensity = stoneBase * stonePulse;
		}
		for (const light of targets.emberLights) {
			light.intensity = emberBase * (0.92 + (firePulse - 0.84) * 0.6);
		}
		for (const material of targets.emissiveMaterials) {
			material.emissiveIntensity = 0.55 + eveningMix * 0.3 + firePulse * 0.12;
		}
		for (const material of targets.stoneMaterials) {
			material.emissiveIntensity =
				0.09 + eveningMix * 0.06 + stonePulse * (0.025 + eveningMix * 0.055);
		}
	}

	for (const sprite of targets.steamSprites) {
		const phase = sprite.userData.phase ?? 0;
		const material = sprite.material;
		if (!runtimeProfile.steamEnabled) {
			material.opacity = 0;
			continue;
		}
		const drift = t * 0.38 + phase;
		material.opacity =
			0.018 +
			(0.014 + eveningMix * 0.008) * (0.5 + 0.5 * Math.sin(drift * 1.7));
		sprite.position.x =
			(sprite.userData.baseX ?? sprite.position.x) +
			Math.sin(drift * 1.3) * 0.018;
		sprite.position.y =
			(sprite.userData.baseY ?? sprite.position.y) +
			0.04 +
			Math.sin(drift * 1.1) * 0.018;
		sprite.position.z =
			(sprite.userData.baseZ ?? sprite.position.z) +
			Math.cos(drift * 1.4) * 0.012;
		sprite.scale.setScalar(0.26 + Math.sin(drift * 1.2) * 0.018);
		sprite.scale.y = 0.34 + Math.cos(drift * 0.9) * 0.025;
	}

	return true;
}
